import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import "./VIPDossier.css";
import paintingsData from "../data/paintings.json";

const VIPDossier = () => {
    const { id } = useParams();
    const [painting, setPainting] = useState(null);
    const [loading, setLoading] = useState(true);
    
    // UI States
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);

    // Form Submission States
    const GOOGLE_SCRIPT_URL = "https://script.google.com/macros/s/AKfycbzGDRaSAjU2NyGr6YZLkpSo9oR0jGz2qzjd3qnwDWFB1U8lkqN0RRzArfQddpZS0dBuDA/exec"; 
    
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        phone: "",
        message: ""
    });

    // Audio Narrative States 
    const audioRef = useRef(null);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [currentLang, setCurrentLang] = useState(null);

    // --- IMAGE PROTECTION SECURITY HOOK ---
    useEffect(() => {
        const handleContextMenu = (e) => {
            // Block right-click context menu specifically on images
            if (e.target.tagName === 'IMG') {
                e.preventDefault();
            }
        };

        document.addEventListener('contextmenu', handleContextMenu);
        return () => {
            document.removeEventListener('contextmenu', handleContextMenu);
        };
    }, []);

    useEffect(() => {
        window.scrollTo(0, 0);
        setLoading(true);

        const foundPainting = paintingsData.find((p) => p.id === parseInt(id));
        setPainting(foundPainting || null);

        if (foundPainting) {
            document.title = `${foundPainting.title} | VIP Exhibition Dossier`;
            // Set the default message once the painting is loaded
            setFormData(prev => ({
                ...prev,
                message: `I am interested in acquiring '${foundPainting.title}'.`
            }));
        }
        
        setIsExpanded(false);

        const timer = setTimeout(() => {
            setLoading(false);
        }, 1200);

        return () => clearTimeout(timer);
    }, [id]);

    useEffect(() => {
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.src = "";
            }
        };
    }, []);

    const handleStop = () => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        }
        setIsSpeaking(false);
        setCurrentLang(null);
    };

    const handleSpeak = (langCode) => {
        handleStop(); 

        let audioUrl = langCode === 'hi-IN' ? painting?.audioHi : painting?.audioEn;

        if (!audioUrl) {
            alert(`Audio narration is currently unavailable for this selection.`);
            return;
        }

        const newAudio = new Audio(audioUrl);
        audioRef.current = newAudio;
        setIsSpeaking(true);
        setCurrentLang(langCode);

        newAudio.onended = () => {
            setIsSpeaking(false);
            setCurrentLang(null);
        };

        newAudio.play().catch(err => {
            console.error("Audio playback failed:", err);
            setIsSpeaking(false);
            setCurrentLang(null);
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        const payload = {
            paintingTitle: painting?.title,
            name: formData.name,
            phone: formData.phone,
            message: formData.message
        };

        try {
            // Added mode: "no-cors" to bypass Google's strict security blocks
            await fetch(GOOGLE_SCRIPT_URL, {
                method: "POST",
                mode: "no-cors", 
                body: JSON.stringify(payload),
                headers: {
                    "Content-Type": "text/plain;charset=utf-8", 
                }
            });

            alert("Thank you. The studio concierge has received your inquiry and will contact you shortly.");
            setIsFormOpen(false);
            setFormData(prev => ({...prev, name: "", phone: ""}));
            
        } catch (error) {
            console.error("Error submitting form:", error);
            alert("There was an issue submitting your request. Please use the WhatsApp option or try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!painting && !loading) {
        return <div style={{ padding: "100px 20px", textAlign: "center", color: "#fff" }}>Dossier not found.</div>;
    }

    const mainImageUrl = painting?.images?.[0]?.imageUrl || "";
    const hoverImageUrl = painting?.images?.[1]?.imageUrl || null;
    
    // Text Truncation Logic
    const rawStory = painting?.story || "A profound narrative captured through unparalleled texture and form.";
    const stripHtml = (html) => html ? html.replace(/<[^>]*>?/gm, '') : '';
    const plainTextStory = stripHtml(rawStory);
    const maxLength = 250;
    const isLongText = plainTextStory.length > maxLength;

    return (
        <div className="vip-dossier-page">
            <AnimatePresence>
                {loading && (
                    <motion.div
                        initial={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.6 }}
                        style={{
                            position: "fixed", inset: 0, zIndex: 99999,
                            backgroundColor: "#050505", display: "flex",
                            justifyContent: "center", alignItems: "center"
                        }}
                    >
                        <span style={{ fontFamily: "'Cormorant Garamond', serif", letterSpacing: "4px", textTransform: "uppercase", color: "#fff" }}>
                            Accessing Secure Dossier...
                        </span>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* MINIMALIST HEADER */}
            <header className="vip-dossier-header">
                <div className="vip-brand">Anjaan Vivek</div>
                <div className="vip-status">Viewing Room</div>
            </header>

            {painting && (
                <main className="vip-dossier-content">
                    
                    <motion.div className="vip-art-verification" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                        <img src={mainImageUrl} alt={painting.title} className="vip-base-img" />
                        {hoverImageUrl && (
                            <img src={hoverImageUrl} alt={`${painting.title} alternate`} className="vip-hover-img" />
                        )}
                    </motion.div>

                    <motion.h1 className="vip-title" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
                        {painting.title}
                    </motion.h1>
                    <motion.div className="vip-artist" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
                        Original Masterwork by Anjaan Vivek
                    </motion.div>

                    <motion.div className="vip-specs" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}>
                        <span>{painting.material}</span>
                        <span className="vip-spec-dot">•</span>
                        <span>{painting.size}</span>
                        <span className="vip-spec-dot">•</span>
                        <span>{painting.year}</span>
                    </motion.div>

                    {/* MUSEUM AUDIO GUIDE */}
                    {(painting?.audioEn?.length > 0 || painting?.audioHi?.length > 0) && (
                        <motion.div className="vip-audio-guide" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}>
                            <span className="vip-audio-label"><i className="fa-solid fa-headphones"></i> Official Audio Guide</span>
                            <div className="vip-audio-controls">
                                {painting?.audioEn?.length > 0 && (
                                    <button 
                                        className={`vip-btn-audio ${currentLang === 'en-US' && isSpeaking ? 'active' : ''}`}
                                        onClick={() => currentLang === 'en-US' && isSpeaking ? handleStop() : handleSpeak('en-US')}
                                    >
                                        <i className={`fa-solid ${currentLang === 'en-US' && isSpeaking ? 'fa-stop' : 'fa-play'}`}></i> English
                                    </button>
                                )}
                                {painting?.audioHi?.length > 0 && (
                                    <button 
                                        className={`vip-btn-audio ${currentLang === 'hi-IN' && isSpeaking ? 'active' : ''}`}
                                        onClick={() => currentLang === 'hi-IN' && isSpeaking ? handleStop() : handleSpeak('hi-IN')}
                                    >
                                        <i className={`fa-solid ${currentLang === 'hi-IN' && isSpeaking ? 'fa-stop' : 'fa-play'}`}></i> हिंदी
                                    </button>
                                )}
                            </div>
                        </motion.div>
                    )}

                    {/* CURATORIAL STORY */}
                    <motion.div 
                        className="vip-story"
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}
                    >
                        {isExpanded || !isLongText ? (
                            <div dangerouslySetInnerHTML={{ __html: rawStory }} />
                        ) : (
                            <p style={{ whiteSpace: "pre-line", marginBottom: 0 }}>
                                {plainTextStory.substring(0, maxLength)}...
                            </p>
                        )}

                        {isLongText && (
                            <span 
                                className="vip-read-more"
                                onClick={() => setIsExpanded(!isExpanded)}
                            >
                                {isExpanded ? "— Read Less" : "+ Read More"}
                            </span>
                        )}
                    </motion.div>

                    {/* DISCREET INQUIRY */}
                    {!painting.sold && (
                        <motion.div className="vip-inquiry-section" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }}>
                            <p>For private acquisition details, provenance records, or to arrange a secure transfer, please connect with the studio concierge.</p>
                            
                            <div className="vip-inquiry-actions">
                      
                                {/* Option 2: Direct WhatsApp Concierge Line */}
                                <a 
                                    href={`https://wa.me/919300847841?text=${encodeURIComponent(`Dear Studio Concierge,\n\nI am currently viewing '${painting?.title}' at the exhibition and would like to request the private provenance and acquisition details.`)}`} 
                                    target="_blank" 
                                    rel="noreferrer" 
                                    className="vip-btn-concierge vip-btn-whatsapp"
                                >
                                    <i className="fa-brands fa-whatsapp"></i> Message Concierge Line
                                </a>
                            </div>
                        </motion.div>
                    )}
                </main>
            )}

            {/* MINIMALIST FOOTER */}
            <footer className="vip-mini-footer">
                <p>&copy; {new Date().getFullYear()} Vivek Kumar Verma 'Anjaan'</p>
                <p style={{ opacity: 0.5, marginTop: "5px" }}>Private Exhibition Archive</p>
            </footer>

            {/* PRIVATE INQUIRY FORM MODAL */}
            <AnimatePresence>
                {isFormOpen && (
                    <motion.div 
                        className="vip-form-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setIsFormOpen(false)}
                    >
                        <motion.div 
                            className="vip-form-box"
                            initial={{ y: 50, opacity: 0, scale: 0.95 }}
                            animate={{ y: 0, opacity: 1, scale: 1 }}
                            exit={{ y: 20, opacity: 0, scale: 0.95 }}
                            transition={{ duration: 0.3 }}
                            onClick={(e) => e.stopPropagation()} 
                        >
                            <button className="vip-form-close" onClick={() => setIsFormOpen(false)}>&times;</button>
                            <h3>Acquisition Inquiry</h3>
                            <p>Please provide your details below. The studio director will reach out to you discreetly.</p>

                            <form onSubmit={handleFormSubmit}>
                                <div className="vip-input-group">
                                    <input 
                                        type="text" 
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        placeholder="Full Name" 
                                        required 
                                    />
                                </div>
                                <div className="vip-input-group">
                                    <input 
                                        type="tel" 
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                        placeholder="Phone Number (with country code)" 
                                        required 
                                    />
                                </div>
                                <div className="vip-input-group">
                                    <textarea 
                                        name="message"
                                        value={formData.message}
                                        onChange={handleInputChange}
                                        placeholder="Any specific questions regarding provenance or delivery?"
                                        required
                                    ></textarea>
                                </div>
                                <button type="submit" className="vip-submit-btn" disabled={isSubmitting}>
                                    {isSubmitting ? "Submitting..." : "Secure Submission"}
                                </button>
                            </form>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default VIPDossier;